import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, CreditCard, Loader2, Shield } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import IntervalToggle from './subscribe/IntervalToggle';
import PlansGrid from './subscribe/PlansGrid';
import SignupForm from './subscribe/SignupForm';

const fmtEUR = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// Repli si /stripe/catalog ne repond pas : la grille du 21/09/2026, avec les trois
// rythmes (mensuel, 3 mois -10 %, annuel -20 %). Doit rester d'accord avec le serveur.
const PLAN_FALLBACK = [
  { key: 'solo', name: 'Solo', price_eur: 69, trial: false, interval: 'month' },
  { key: 'starter', name: 'Starter', price_eur: 109, trial: false, interval: 'month' },
  { key: 'business', name: 'Business', price_eur: 249, trial: false, interval: 'month' },
  { key: 'enterprise', name: 'Enterprise', price_eur: 449, trial: false, interval: 'month' },
  { key: 'solo_quarterly', name: 'Solo', price_eur: 186.3, interval: 'quarter', monthly_key: 'solo' },
  { key: 'starter_quarterly', name: 'Starter', price_eur: 294.3, interval: 'quarter', monthly_key: 'starter' },
  { key: 'business_quarterly', name: 'Business', price_eur: 672.3, interval: 'quarter', monthly_key: 'business' },
  { key: 'enterprise_quarterly', name: 'Enterprise', price_eur: 1212.3, interval: 'quarter', monthly_key: 'enterprise' },
  { key: 'solo_yearly', name: 'Solo', price_eur: 662.4, interval: 'year', monthly_key: 'solo' },
  { key: 'starter_yearly', name: 'Starter', price_eur: 1046.4, interval: 'year', monthly_key: 'starter' },
  { key: 'business_yearly', name: 'Business', price_eur: 2390.4, interval: 'year', monthly_key: 'business' },
  { key: 'enterprise_yearly', name: 'Enterprise', price_eur: 4310.4, interval: 'year', monthly_key: 'enterprise' },
];

export default function Subscribe() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user, agency } = useAuth();
  const [catalog, setCatalog] = useState(PLAN_FALLBACK);
  const [interval, setInterval] = useState('month');
  const [selectedKey, setSelectedKey] = useState(params.get('plan') || 'solo');
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization: '',
    accept: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const captchaRef = React.useRef(null);

  useEffect(() => {
    let active = true;
    api.get('/stripe/catalog').then(({ data }) => { if (active && data.plans?.length) setCatalog(data.plans); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const monthlyPlans = useMemo(
    () => catalog.filter((p) => (p.interval || 'month') === 'month'),
    [catalog],
  );
  const selected = useMemo(
    () => monthlyPlans.find((p) => p.key === selectedKey) || monthlyPlans[0],
    [monthlyPlans, selectedKey],
  );
  const yearlyTwin = useMemo(
    () => catalog.find((p) => p.key === `${selectedKey}_yearly`),
    [catalog, selectedKey],
  );
  // 25/09 : le trimestriel (-10 %) s'ajoute au mensuel et a l'annuel.
  const quarterTwin = useMemo(
    () => catalog.find((p) => p.key === `${selectedKey}_quarterly`),
    [catalog, selectedKey],
  );
  const jumeau = interval === 'year' ? yearlyTwin : interval === 'quarter' ? quarterTwin : null;
  const moisPayes = interval === 'year' ? 12 : interval === 'quarter' ? 3 : 1;
  const checkoutPlanKey = jumeau ? jumeau.key : selectedKey;
  // Ce qu'il economise sur la duree de l'engagement, par rapport au mensuel.
  const yearlySavings = selected && jumeau
    ? Math.round(selected.price_eur * moisPayes - jumeau.price_eur) : 0;
  const upgradePrice = jumeau ? jumeau.price_eur / moisPayes : (selected?.price_eur || 0);

  const isLoggedIn = !!user && !!agency;

  const upgradeNow = () => {
    setBusy(true);
    setErr(null);
    api.post('/stripe/checkout', { plan: checkoutPlanKey, origin: window.location.origin })
      .then(({ data }) => { window.location.href = data.url; })
      .catch((e) => { setErr(apiError(e, 'Le paiement n a pas pu demarrer')); setBusy(false); });
  };

  const signupThenCheckout = async (e) => {
    e?.preventDefault?.();
    if (!form.accept) { setErr('Merci d accepter les conditions d utilisation.'); return; }
    if (form.password.length < 8) { setErr('Le mot de passe doit faire au moins 8 caracteres.'); return; }
    setBusy(true);
    setErr(null);
    try {
      // Fresh, single-use captcha token at submit time (see Turnstile.jsx).
      let token;
      if (TURNSTILE_SITE_KEY) {
        token = await captchaRef.current?.getToken();
        if (!token) {
          setErr(captchaRef.current?.scriptBlocked()
            ? 'Security check blocked — please disable your ad-blocker for this site, then retry.'
            : 'Captcha check failed — please try again.');
          setBusy(false); return;
        }
      }
      let referral_code;
      try { referral_code = localStorage.getItem('chathub_ref') || undefined; } catch { /* ignore */ }
      await api.post('/auth/register', {
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        organization: form.organization.trim() || `${form.first_name} ${form.last_name}`.trim(),
        referral_code,
        turnstile_token: token || undefined,
      });
      const { data } = await api.post('/stripe/checkout', { plan: checkoutPlanKey, origin: window.location.origin });
      window.location.href = data.url;
    } catch (e2) {
      // Token already consumed by the attempt — refresh for the next try.
      captchaRef.current?.reset();
      setErr(apiError(e2, 'Sign-up failed'));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-slate-200">
      <header className="border-b border-white/5 backdrop-blur-md sticky top-0 z-30 bg-[#08080A]/85">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="subscribe-home" className="flex items-center gap-2">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {!isLoggedIn && (
              <Link to="/auth/login" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-center mb-8 max-w-2xl mx-auto">
          <Badge variant="outline" className="border-indigo-500/40 bg-indigo-500/10 text-indigo-300 mb-3">
            {t('pricing.kicker')}
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
            {t('cta.title1')} {t('cta.title2')}
          </h1>
          <p className="text-slate-400 mt-3 text-sm leading-relaxed">
            {t('pricing.sub')}
          </p>
        </div>

        <IntervalToggle interval={interval} setInterval={setInterval} />

        <PlansGrid
          monthlyPlans={monthlyPlans}
          catalog={catalog}
          selectedKey={selectedKey}
          setSelectedKey={setSelectedKey}
          interval={interval}
          fmtEUR={fmtEUR}
          t={t}
        />

        <div className="max-w-2xl mx-auto rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/[0.05] to-transparent p-6 md:p-8">
          <h2 className="font-display text-2xl font-bold text-white mb-1">
            {isLoggedIn ? `Passer au plan ${selected?.name || 'choisi'}` : 'Crée ton compte et abonne-toi'}
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            {isLoggedIn ? `Tu es connecté avec ${user?.email}. Un seul clic ci-dessous pour démarrer.` : 'Création du compte et paiement en une seule étape.'}
          </p>

          {err && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.08] text-rose-200 px-3 py-2 text-sm mb-4" data-testid="subscribe-error">
              {err}
            </div>
          )}

          {isLoggedIn ? (
            <Button onClick={upgradeNow} disabled={busy} data-testid="subscribe-upgrade" className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white h-12 text-base font-medium shadow-lg shadow-indigo-500/30">
              {busy ? <Loader2 size={18} className="mr-2 animate-spin" /> : <CreditCard size={18} className="mr-2" />}
              {`S'abonner — ${fmtEUR(upgradePrice)}/mois`}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <SignupForm
              form={form}
              setForm={setForm}
              onSubmit={signupThenCheckout}
              busy={busy}
              selected={selected}
              interval={interval}
              fmtEUR={fmtEUR}
              displayPrice={upgradePrice}
              captcha={TURNSTILE_SITE_KEY ? <Turnstile ref={captchaRef} /> : null}
            />
          )}

          <div className="mt-6 pt-5 border-t border-white/5 grid grid-cols-2 gap-2 text-xs text-slate-400">
            <div className="flex items-center gap-2 text-emerald-300 font-medium"><Shield size={14} /> Paiement sécurisé par Stripe</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Résiliable à tout moment</div>
            {interval !== 'month' && yearlySavings > 0 && <div className="flex items-center gap-2 text-emerald-300"><Check size={12} className="text-emerald-400" /> Économie de {fmtEUR(yearlySavings)} {interval === 'year' ? 'par an' : 'tous les 3 mois'}</div>}
            {interval === 'month' && <div className="flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Résiliable à tout moment</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
